import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../App';

const API_URL = process.env.REACT_APP_API_BASE_URL

const Products = () => {
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch((API_URL) + '/products/get-products');
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { id: 'all', name: t.products.category.all },
    { id: 'Fertilizers', name: t.products.category.fertilizers },
    { id: 'Seeds', name: t.products.category.seeds },
    { id: 'Pesticides', name: t.products.category.pesticides },
    { id: 'Tools', name: t.products.category.equipment },
    { id: 'Other', name: 'Other' }
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  // Helper function to check if image is a URL or emoji
  const isImageUrl = (image) => {
    return image && (image.startsWith('http://') || image.startsWith('https://'));
  };

  // Handle image load error
  const handleImageError = (productId) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-green-500 text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.products.title}</h1>
          <p className="text-xl text-green-100">{t.products.subtitle}</p>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-green-700 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-green-50 shadow-md'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Loading products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map(product => (
                <div key={product._id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="bg-gradient-to-br from-green-100 to-green-200 h-48 flex items-center justify-center">
                    {isImageUrl(product.image) && !imageErrors[product._id] ? (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(product._id)}
                      />
                    ) : (
                      <div className="text-8xl">{product.image || '🌾'}</div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{product.name}</h3>
                      {product.inStock ? (
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                          {t.products.inStock}
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                          {t.products.outOfStock}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4 text-sm">{product.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-green-700">₹{product.price}</span>
                      <Link 
                        to="/contact" 
                        className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-sm font-semibold"
                      >
                        {t.products.inquire}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Information Banner */}
      <section className="bg-green-800 text-white py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Looking for a Specific Product?
          </h2>
          <p className="text-lg text-green-100 mb-6">
            We can source specialized agricultural inputs based on your requirements
          </p>
          <Link to="/contact" className="btn-secondary inline-block">
            Contact Us for Special Orders
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;